import type { HistogramBucket } from './HistoricalViewContainer';

/** Weekly prices of one station, as in `historicalView.utilizationSeries.buckets`. */
export type StationPriceSeries = readonly { readonly endDate: string; readonly price?: number | null }[];

/** Linear-interpolation percentile (`p` from 0 to 1) of the values, or null when there is none. */
export function percentile(values: readonly number[], p: number): number | null {
  if (!values.length) {
    return null;
  }
  const sorted = [...values].sort((a, b) => a - b);
  const position = (sorted.length - 1) * p;
  const lower = Math.floor(position);
  const upper = Math.ceil(position);
  return sorted[lower] + (sorted[upper] - sorted[lower]) * (position - lower);
}

export const median = (values: readonly number[]) => percentile(values, 0.5);

/** The aggregate and the station series may not format the same instant the same way. */
const weekKey = (endDate: string) => {
  const time = new Date(endDate).getTime();
  return Number.isNaN(time) ? endDate : String(time);
};

/** Station prices of each displayed week: only stations with a numeric price that week. */
function stationPricesByWeek(buckets: readonly HistogramBucket[], stations: readonly StationPriceSeries[]): Map<string, number[]> {
  const byWeek = new Map(buckets.map((b) => [weekKey(b.endDate), [] as number[]]));
  for (const series of stations) {
    for (const { endDate, price } of series) {
      if (typeof price === 'number' && Number.isFinite(price)) {
        byWeek.get(weekKey(endDate))?.push(price);
      }
    }
  }
  return byWeek;
}

/**
 * Adds the weekly median (medianPrice) and the middle 50% (p25Price–p75Price) of the station prices to each
 * week, over the same station prices. The median needs one price, the band two: a week without enough prices
 * gets none rather than an invented one, and never the API average (`price`, kept for the station sheet).
 */
export function withMiddleBand(buckets: readonly HistogramBucket[], stations: readonly StationPriceSeries[]): HistogramBucket[] {
  const byWeek = stationPricesByWeek(buckets, stations);
  return buckets.map((bucket) => {
    const prices = byWeek.get(weekKey(bucket.endDate)) ?? [];
    const hasBand = prices.length >= 2;
    return {
      ...bucket,
      medianPrice: median(prices) ?? undefined,
      p25Price: hasBand ? (percentile(prices, 0.25) ?? undefined) : undefined,
      p75Price: hasBand ? (percentile(prices, 0.75) ?? undefined) : undefined,
    };
  });
}

export type PriceAxis = { domain: [number, number]; ticks: number[] };

/** Smallest span of the price axis, in cents: a few cents must not fill the chart height. */
const MIN_SPAN_CENTS = 20;

/**
 * Price axis covering every value (band and line), padded by a cent and rounded outwards to $0.05 steps
 * ($0.10 beyond a $0.60 span), with a tick at both ends and in the middle. E.g. $0.30–$0.83 → $0.25–$0.85.
 */
export function priceAxis(values: readonly number[]): PriceAxis | undefined {
  const cents = values.filter((v) => Number.isFinite(v)).map((v) => Math.round(v * 10000) / 100);
  if (!cents.length) {
    return undefined;
  }
  let low = Math.min(...cents) - 1;
  let high = Math.max(...cents) + 1;
  if (high - low < MIN_SPAN_CENTS) {
    const middle = (low + high) / 2;
    low = middle - MIN_SPAN_CENTS / 2;
    high = middle + MIN_SPAN_CENTS / 2;
  }
  low = Math.max(0, low);
  const step = high - low > 60 ? 10 : 5;
  low = Math.floor(low / step) * step;
  high = Math.ceil(high / step) * step;
  // an even number of steps keeps the middle tick on the grid
  if ((high - low) % (2 * step) !== 0) {
    high += step;
  }
  const toDollars = (c: number) => Math.round(c) / 100;
  return { domain: [toDollars(low), toDollars(high)], ticks: [low, (low + high) / 2, high].map(toDollars) };
}

/** Weekly prices of the Area Summary charging price chart, in USD per kWh. */
export type WeeklyPriceBand = { readonly median?: number; readonly p25?: number; readonly p75?: number };

/** Smallest span of the charging price axis, in cents. */
const CHARGING_MIN_SPAN_CENTS = 25;
const CHARGING_STEP_CENTS = 5;

/**
 * Charging price axis: it fits the middle 50% (p25–p75), not the min–max, padded by $0.05 and rounded outwards
 * to $0.05; below a $0.25 span both ends are widened equally, then rounded outwards again. Computed in cents to
 * avoid float error. A week with a median but no band counts its median, so that its line is never clipped.
 * E.g. p25 $0.47 to p75 $0.58 → $0.40–$0.65.
 */
export function chargingPriceDomain(weeks: readonly WeeklyPriceBand[]): [number, number] | undefined {
  const cents = (v: number | undefined) => (v !== undefined && Number.isFinite(v) ? [Math.round(v * 10000) / 100] : []);
  const lows = weeks.flatMap((w) => (w.p25 !== undefined && w.p75 !== undefined ? cents(w.p25) : cents(w.median)));
  const highs = weeks.flatMap((w) => (w.p25 !== undefined && w.p75 !== undefined ? cents(w.p75) : cents(w.median)));
  if (!lows.length || !highs.length) {
    return undefined;
  }
  const step = CHARGING_STEP_CENTS;
  let low = Math.floor((Math.min(...lows) - step) / step) * step;
  let high = Math.ceil((Math.max(...highs) + step) / step) * step;
  if (high - low < CHARGING_MIN_SPAN_CENTS) {
    const widen = (CHARGING_MIN_SPAN_CENTS - (high - low)) / 2;
    low = Math.floor((low - widen) / step) * step;
    high = Math.ceil((high + widen) / step) * step;
  }
  // prices are never negative: the span lost below $0 is added on top
  if (low < 0) {
    high -= low;
    low = 0;
  }
  return [low / 100, high / 100];
}

/** Gridlines of the charging price axis: every $0.10 on round values inside the domain, ends included. */
export function chargingPriceTicks([low, high]: readonly [number, number]): number[] {
  const ticks: number[] = [];
  for (let cents = Math.ceil(Math.round(low * 100) / 10) * 10; cents <= Math.round(high * 100); cents += 10) {
    ticks.push(cents / 100);
  }
  return ticks;
}
